// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import useFetch from "@/hooks/useFetch";
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import type { TyApiResponse, TyLang, TyStoreDetailResponse, TySubscription } from "@/types";
import StatusBadge from "@/UI/atoms/StatusBadge";
import ActionsBar from "@/UI/components/ActionsBar";
import { buildMenuUrl, getMenuHost } from "@/utils";
import ActivitySection from "./ActivitySection";
import Loader from "./Loader";
import "./styles.css";

// --- Helpers ------------------------------------------------------------------------------------

function pickLang(items: TyLang[] | undefined, lang: string): string {
    if (!items?.length) return "";
    return (items.find((i) => i.lang === lang) ?? items.find((i) => i.default) ?? items[0]).value;
}

function formatDate(value: string | null, lang: string): string {
    if (!value) return "—";
    return new Date(value).toLocaleDateString(lang === "en" ? "en-GB" : "ar-EG", { year: "numeric", month: "short", day: "numeric" });
}

function daysBetween(from: Date, to: Date): number {
    return Math.max(0, Math.ceil((to.getTime() - from.getTime()) / 86_400_000));
}

// --- Logic --------------------------------------------------------------------------------------

export default function OneStoreScreen() {
    const t = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const lang = useLang((state) => state.lang) ?? "ar";

    const [copied, setCopied] = useState(false);

    const { data, loading, error, invoke } = useFetch<TyApiResponse<TyStoreDetailResponse>>(`stores/${id}`, { method: "GET" });

    useEffect(() => {
        if (!copied) return;
        const timer = setTimeout(() => setCopied(false), 2000);
        return () => clearTimeout(timer);
    }, [copied]);

    if (loading) return <Loader />;

    if (error || !data?.data) {
        return (
            <div className="one-store-empty">
                <p className="one-store-empty-title">{error ?? t.storeDetail.notFound}</p>
                <button type="button" className="one-store-retry" onClick={() => invoke()}>
                    {t.storeDetail.retry}
                </button>
            </div>
        );
    }

    const { store, menu, branches, clients, subscription, subscriptionHistory, support } = data.data;

    const menuUrl = buildMenuUrl(store.slug);
    const storeName = pickLang(store.name, lang) || store.slug;
    const description = pickLang(store.description, lang);

    const handleCopy = async () => {
        await navigator.clipboard.writeText(menuUrl);
        setCopied(true);
    };

    /** Earlier rows only — the live one already has its own panel above. */
    const history = subscriptionHistory.filter((row: TySubscription) => row._id !== subscription?._id);

    return (
        <div className="one-store-screen">
            <ActionsBar title={storeName} onBack={() => navigate("/stores")} />

            {/* --- Identity ------------------------------------------------------------------- */}
            <section className="one-store-head">
                <div className="one-store-logo">
                    {store.logo ? (
                        <img src={store.logo} alt={storeName} />
                    ) : (
                        <img className="one-store-logo-fallback" src="/icons/store-icon.svg" alt="" />
                    )}
                </div>

                <div className="one-store-head-main">
                    <div className="one-store-head-title">
                        <h1 className="one-store-name">{storeName}</h1>
                        <StatusBadge status={subscription?.status ?? null} />
                    </div>
                    {description && <p className="one-store-description">{description}</p>}
                    <p className="one-store-meta">
                        {t.enums.storeType[store.type]} · {t.storeDetail.createdAt} {formatDate(store.createdAt, lang)}
                    </p>
                    <p className="one-store-host" dir="ltr">
                        {getMenuHost(store.slug)}
                    </p>
                </div>

                <div className="one-store-head-actions">
                    <a className="one-store-action one-store-action--primary" href={menuUrl} target="_blank" rel="noreferrer">
                        {t.storeDetail.openMenu}
                    </a>
                    <button type="button" className="one-store-action" onClick={handleCopy}>
                        {copied ? t.storeDetail.copied : t.storeDetail.copyLink}
                    </button>
                </div>
            </section>

            {/* --- Menu ----------------------------------------------------------------------- */}
            <section className="one-store-section">
                <p className="one-store-section-title">{t.storeDetail.sectionMenu}</p>
                <div className="one-store-stats">
                    {(
                        [
                            [t.storeDetail.menuProducts, menu.products],
                            [t.storeDetail.menuActive, menu.activeProducts],
                            [t.storeDetail.menuHidden, menu.hiddenProducts],
                            [t.storeDetail.menuDiscounted, menu.discountedProducts],
                            [t.storeDetail.menuUnpriced, menu.unpricedProducts],
                            [t.storeDetail.menuCategories, menu.categories],
                        ] as const
                    ).map(([label, value]) => (
                        <div key={label} className="one-store-stat">
                            <p className="one-store-stat-value">{value}</p>
                            <p className="one-store-stat-label">{label}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* --- Subscription --------------------------------------------------------------- */}
            <section className="one-store-section">
                <p className="one-store-section-title">{t.storeDetail.sectionSubscription}</p>

                {!subscription ? (
                    <p className="one-store-note">{t.storeDetail.subNone}</p>
                ) : (
                    <div className="one-store-rows">
                        <div className="one-store-row">
                            <span>{t.storeDetail.subPeriod}</span>
                            <strong>{t.enums.subscriptionPeriod[subscription.period]}</strong>
                        </div>
                        <div className="one-store-row">
                            <span>{t.storeDetail.subPrice}</span>
                            <strong>
                                {subscription.price} {t.storeDetail.currency}
                            </strong>
                        </div>
                        <div className="one-store-row">
                            <span>{t.storeDetail.subBranches}</span>
                            <strong>{subscription.branches}</strong>
                        </div>
                        <div className="one-store-row">
                            <span>{t.storeDetail.subStartDate}</span>
                            <strong>{formatDate(subscription.startDate, lang)}</strong>
                        </div>
                        <div className="one-store-row">
                            <span>{t.storeDetail.subEndDate}</span>
                            <strong>{formatDate(subscription.endDate, lang)}</strong>
                        </div>
                        {subscription.endDate && subscription.status === "active" && (
                            <div className="one-store-row">
                                <span>{t.storeDetail.subDaysLeft}</span>
                                <strong>
                                    {daysBetween(new Date(), new Date(subscription.endDate))} {t.storeDetail.days}
                                </strong>
                            </div>
                        )}
                        {subscription.paymentDueAt && (
                            <div className="one-store-row">
                                <span>{t.storeDetail.subPaymentDue}</span>
                                <strong>{formatDate(subscription.paymentDueAt, lang)}</strong>
                            </div>
                        )}
                        {subscription.coupon && (
                            <div className="one-store-row">
                                <span>{t.storeDetail.subCoupon}</span>
                                <strong dir="ltr">{subscription.coupon.code}</strong>
                            </div>
                        )}
                    </div>
                )}

                <p className="one-store-sub-title">{t.storeDetail.subHistory}</p>
                {history.length === 0 ? (
                    <p className="one-store-note">{t.storeDetail.subHistoryEmpty}</p>
                ) : (
                    <div className="one-store-history">
                        {history.map((row) => (
                            <div key={row._id} className="one-store-history-row">
                                <StatusBadge status={row.status} />
                                <span className="one-store-history-period">{t.enums.subscriptionPeriod[row.period]}</span>
                                <span className="one-store-history-dates">
                                    {formatDate(row.startDate, lang)} → {formatDate(row.endDate, lang)}
                                </span>
                                <span className="one-store-history-price">
                                    {row.price} {t.storeDetail.currency}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* --- Branches ------------------------------------------------------------------- */}
            <section className="one-store-section">
                <p className="one-store-section-title">
                    {t.storeDetail.sectionBranches} <span className="one-store-count">{branches.length}</span>
                </p>

                {branches.length === 0 ? (
                    <p className="one-store-note">{t.storeDetail.branchesEmpty}</p>
                ) : (
                    <div className="one-store-tiles">
                        {branches.map((branch) => (
                            <div key={branch._id} className="one-store-tile">
                                <p className="one-store-tile-title">{branch.name || branch.slug}</p>
                                <a
                                    className="one-store-tile-link"
                                    href={buildMenuUrl(store.slug, branch.slug)}
                                    target="_blank"
                                    rel="noreferrer"
                                    dir="ltr"
                                >
                                    ?branch={branch.slug}
                                </a>
                                {branch.address && <p className="one-store-tile-sub">{branch.address}</p>}
                                <p className="one-store-tile-sub">
                                    {branch.inActiveCount} {t.storeDetail.branchInactive} · {branch.hiddenCount} {t.storeDetail.branchHidden} ·{" "}
                                    {branch.pricesCount} {t.storeDetail.branchPrices}
                                </p>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* --- Clients -------------------------------------------------------------------- */}
            <section className="one-store-section">
                <p className="one-store-section-title">
                    {t.storeDetail.sectionClients} <span className="one-store-count">{clients.length}</span>
                </p>

                {clients.length === 0 ? (
                    <p className="one-store-note">{t.storeDetail.clientsEmpty}</p>
                ) : (
                    <div className="one-store-tiles">
                        {clients.map((client) => (
                            <div key={client._id} className="one-store-tile">
                                <div className="one-store-tile-head">
                                    <p className="one-store-tile-title">{client.name}</p>
                                    <span className={`one-store-role${client.isOwner ? " one-store-role--owner" : ""}`}>
                                        {client.isOwner ? t.storeDetail.owner : t.storeDetail.staff}
                                    </span>
                                </div>
                                <p className="one-store-tile-sub" dir="ltr">
                                    {client.phone}
                                </p>
                                {!client.isOwner && (
                                    <p className="one-store-tile-sub">
                                        {client.permissions.map((p) => p.model).join(" · ") || "—"}
                                        {client.branches === "all" ? ` · ${t.storeDetail.allBranches}` : ""}
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* --- Support -------------------------------------------------------------------- */}
            <section className="one-store-section">
                <p className="one-store-section-title">{t.storeDetail.sectionSupport}</p>
                <div className="one-store-stats">
                    {(
                        [
                            [t.storeDetail.supportPending, support.pending],
                            [t.storeDetail.supportContacted, support.contacted],
                            [t.storeDetail.supportSolved, support.solved],
                        ] as const
                    ).map(([label, value]) => (
                        <div key={label} className="one-store-stat">
                            <p className="one-store-stat-value">{value}</p>
                            <p className="one-store-stat-label">{label}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* --- Activity ------------------------------------------------------------------- */}
            <ActivitySection storeId={store._id} />
        </div>
    );
}
